/**
 * Live-class provider settings (F3-G), driven by the provider's
 * manifest `configSchema` (registry `@space/shared/live`). Provider select
 * (of the enabled ones) + generated fields. Used for the cohort DEFAULT
 * and for the per-session OVERRIDE (same component, different `onSave`).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { defaultMeetingProviderRegistry, type SettingsField } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";

export interface MeetingSettingsProps {
  title?: string;
  description?: string;
  initialProvider?: string | null;
  initialConfig?: Record<string, unknown> | null;
  /** Persists the chosen provider + config. */
  onSave: (provider: string, config: Record<string, unknown>) => Promise<void>;
}

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export const MeetingSettings: Component<MeetingSettingsProps> = (props) => {
  const { t } = useI18n();
  const registry = defaultMeetingProviderRegistry();
  const providerOptions = registry.all().map((p) => ({
    value: p.manifest.id,
    label: p.manifest.name,
  }));

  const [provider, setProvider] = createSignal<string>(
    props.initialProvider ?? providerOptions[0]?.value ?? "jitsi",
  );
  const [config, setConfig] = createSignal<Record<string, unknown>>({
    ...props.initialConfig,
  });
  const [saving, setSaving] = createSignal(false);

  const schema = createMemo<readonly SettingsField[]>(
    () => registry.get(provider())?.manifest.configSchema ?? [],
  );

  const setField = (key: string, value: unknown) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await props.onSave(provider(), config());
      showToast(t(msg`Live provider saved`), "success");
    } catch {
      showToast(t(msg`Could not save the live provider`), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section class="space-y-3 rounded-2xl border border-border p-4" data-testid="meeting-settings">
      <Show when={props.title}>
        <h3 class="text-sm font-bold">{props.title}</h3>
      </Show>
      <Show when={props.description}>
        <p class="text-xs text-muted-foreground">{props.description}</p>
      </Show>

      <label class="block space-y-1">
        <span class="text-xs font-medium text-muted-foreground">{t(msg`Provider`)}</span>
        <div data-testid="meeting-provider-select">
          <Select
            options={providerOptions}
            value={provider()}
            onChange={(v) => setProvider(v)}
            aria-label={t(msg`Provider`)}
          />
        </div>
      </label>

      <For each={schema()}>
        {(f) => (
          <label class="block space-y-1" data-testid={`meeting-field-${f.key}`}>
            <span class="text-xs font-medium text-muted-foreground">{f.label}</span>
            <Show
              when={f.type !== "boolean"}
              fallback={
                <input
                  type="checkbox"
                  checked={Boolean(config()[f.key])}
                  onChange={(e) => setField(f.key, e.currentTarget.checked)}
                  data-testid={`meeting-input-${f.key}`}
                />
              }
            >
              <input
                class={field}
                type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"}
                placeholder={f.placeholder}
                value={String(config()[f.key] ?? "")}
                onInput={(e) =>
                  setField(
                    f.key,
                    f.type === "number" ? Number(e.currentTarget.value) : e.currentTarget.value,
                  )
                }
                data-testid={`meeting-input-${f.key}`}
              />
            </Show>
            <Show when={f.help}>
              <span class="text-[0.7rem] text-muted-foreground">{f.help}</span>
            </Show>
          </label>
        )}
      </For>

      <Button size="sm" onClick={() => void save()} disabled={saving()} data-testid="meeting-save">
        {t(msg`Save provider`)}
      </Button>
    </section>
  );
};
